import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserProvider, Contract, ethers } from 'ethers';
import { AuthContext } from '../AuthContext';
import { SavingsTab } from './cash/SavingsTab';
import { LendingTab } from './cash/LendingTab';

// Web3Cash Contract Configuration (Sepolia Testnet)
const WEB3CASH_CONTRACT_ADDRESS = '0xABCe40fE1FDd7314F6708A76016a363Fd6b888Cd';
const WEB3CASH_ABI = [
  "function deposit(uint256 amount) external",
  "function withdraw(uint256 amount) external",
  "function emergencyWithdraw() external",
  "function requestLoan(uint256 amount, uint256 collateralAmount, uint256 interestRate, uint256 duration) external",
  "function approveLoan(uint256 loanId) external",
  "function repayLoan(uint256 loanId) external payable",
  "function getSavingsAccount(address user) external view returns (tuple(uint256 balance, uint256 lastUpdateTime, uint256 accruedInterest, bool isActive), uint256)",
  "function getActiveLoan(uint256 loanId) external view returns (tuple(uint256 loanId, uint256 principalAmount, uint256 interestRate, uint256 startTime, uint256 endTime, uint256 collateralAmount, uint256 totalOwed, bool isRepaid))",
  "function getUserLoanIds(address user) external view returns (uint256[])",
  "function updateSavingsAPY(uint256 newAPY) external",
  "function owner() external view returns (address)",
  "function REVENUE_WALLET() external view returns (address)",
  "function FEE_PERCENT() external view returns (uint256)",
  "function FEE_DENOMINATOR() external view returns (uint256)",
  "function USDC() external view returns (address)",
  "function collateralRatio() external view returns (uint256)",
  "function savingsAPY() external view returns (uint256)",
  "function activeLoans(uint256) external view returns (uint256, uint256, uint256, uint256, uint256, uint256, uint256, bool)",
  "function loanRequests(uint256) external view returns (address, uint256, uint256, uint256, uint256, uint256, bool, bool)",
  // Events
  "event Deposit(address indexed user, uint256 amount, uint256 timestamp)",
  "event Withdrawal(address indexed user, uint256 amount, uint256 timestamp)",
  "event LoanRequested(address indexed borrower, uint256 loanId, uint256 amount, uint256 collateral)",
  "event LoanApproved(uint256 indexed loanId, address indexed lender)",
  "event LoanRepaid(uint256 indexed loanId, uint256 amount)",
  "event EmergencyWithdrawal(address indexed user, uint256 amount)"
];

const Web3Cash = () => {
  const { isAuthenticated, ensName, loginWithSIWE } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('savings');
  const [userAddress, setUserAddress] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);
  const [contractData, setContractData] = useState({
    savingsAPY: 0,
    collateralRatio: 0,
    feePercent: 0,
    revenueWallet: '',
    usdcAddress: '',
    totalSavings: 0,
    totalLoans: 0
  });
  const [loading, setLoading] = useState(false);

  // Initialize contract and load data
  useEffect(() => {
    const init = async () => {
      if (window.ethereum && isAuthenticated) {
        try {
          setLoading(true);
          const provider = new BrowserProvider(window.ethereum);
          const network = await provider.getNetwork();

          if (network.chainId !== 11155111n) {
            setError("Please switch to Sepolia testnet (Chain ID: 11155111) to use Web3Cash");
            setLoading(false);
            return;
          }

          const accounts = await provider.send("eth_requestAccounts", []);
          setUserAddress(accounts[0]);
          setIsConnected(true);

          const signer = await provider.getSigner();
          const web3cashContract = new Contract(WEB3CASH_CONTRACT_ADDRESS, WEB3CASH_ABI, signer);
          setContract(web3cashContract);

          // Load contract data
          const [savingsAPY, collateralRatio, feePercent, revenueWallet, usdcAddress] = await Promise.all([
            web3cashContract.savingsAPY(),
            web3cashContract.collateralRatio(),
            web3cashContract.FEE_PERCENT(),
            web3cashContract.REVENUE_WALLET(),
            web3cashContract.USDC()
          ]);

          setContractData({
            savingsAPY: (Number(savingsAPY) / 100).toFixed(2),
            collateralRatio: (Number(collateralRatio) / 100).toFixed(0),
            feePercent: (Number(feePercent) / 100).toFixed(1),
            revenueWallet: revenueWallet,
            usdcAddress: usdcAddress,
            totalSavings: 0, // Will be loaded separately if contract has this function
            totalLoans: 0   // Will be loaded separately if contract has this function
          });

          setError(null);
        } catch (err) {
          console.error('Contract initialization error:', err);
          setError("Failed to connect wallet or initialize contract: " + err.message);
        } finally {
          setLoading(false);
        }
      }
    };
    init();
  }, [isAuthenticated]);

  const connectWallet = async () => {
    try {
      await loginWithSIWE();
    } catch (error) {
      console.error('Wallet connection error:', error);
      setError("Failed to connect wallet: " + error.message);
    }
  };

  // Event listeners for real-time updates
  useEffect(() => {
    if (!contract) return;

    const setupEventListeners = () => {
      try {
        // Deposit event
        contract.on('Deposit', (user, amount, timestamp) => {
          if (user.toLowerCase() === userAddress.toLowerCase()) {
            console.log('Deposit detected:', { user, amount: amount.toString(), timestamp: timestamp.toString() });
            // You can add state updates or notifications here
          }
        });

        // Withdrawal event
        contract.on('Withdrawal', (user, amount, timestamp) => {
          if (user.toLowerCase() === userAddress.toLowerCase()) {
            console.log('Withdrawal detected:', { user, amount: amount.toString(), timestamp: timestamp.toString() });
          }
        });

        // Loan events
        contract.on('LoanRequested', (borrower, loanId, amount, collateral) => {
          if (borrower.toLowerCase() === userAddress.toLowerCase()) {
            console.log('Loan requested:', { loanId: loanId.toString(), amount: amount.toString(), collateral: collateral.toString() });
          }
        });

        contract.on('LoanApproved', (loanId, lender) => {
          console.log('Loan approved:', { loanId: loanId.toString(), lender });
        });

        contract.on('LoanRepaid', (loanId, amount) => {
          console.log('Loan repaid:', { loanId: loanId.toString(), amount: amount.toString() });
        });
      } catch (error) {
        console.error('Error setting up event listeners:', error);
      }
    };

    setupEventListeners();

    // Cleanup event listeners on unmount
    return () => {
      if (contract) {
        contract.removeAllListeners('Deposit');
        contract.removeAllListeners('Withdrawal');
        contract.removeAllListeners('LoanRequested');
        contract.removeAllListeners('LoanApproved');
        contract.removeAllListeners('LoanRepaid');
        contract.removeAllListeners('EmergencyWithdrawal');
      }
    };
  }, [contract, userAddress]);

  const containerStyle = {
    background: '#1a1a1a',
    padding: '2rem',
    borderRadius: '10px',
    boxShadow: '0 0 20px #FFD700',
    transition: 'box-shadow 0.3s ease',
    width: '80%',
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-6">
      <Helmet>
        <title>Web3Cash.eth - Decentralized Savings & Lending | BankSystem</title>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <div style={{ margin: '20px auto' }}>
          <div
            style={containerStyle}
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 0 40px #FFD700')}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 0 20px #FFD700')}
          >
            <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '30px', color: '#FFD700' }}>
              Web3Cash.eth – Your ENS-Gated Cash Hub
            </h1>
            <p style={{ marginBottom: '30px', color: '#FFD700' }}>
              Welcome to Web3Cash, an ENS-gated platform where decentralized lending and savings empower your financial future.
              With total ENS domain gating globally, only .xbsys domain owners can access this secure, transparent system tailored for you.
            </p>

            {/* Connection Status */}
            <div className="mb-6" style={{
              backgroundColor: '#333',
              padding: '1rem',
              borderRadius: '5px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${isAuthenticated ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className="font-medium" style={{ color: isAuthenticated ? '#00FF00' : '#FF0000' }}>
                  {isAuthenticated ? `✓ Wallet Connected: ${ensName} (Authenticated)` : 'Not Connected'}
                </span>
              </div>
              {!isAuthenticated && (
                <button
                  onClick={connectWallet}
                  style={{
                    background: 'transparent',
                    border: '2px solid #FFD700',
                    color: '#FFD700',
                    padding: '15px 30px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#FFD700')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  Connect Wallet
                </button>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div style={{
                background: 'rgba(255,0,0,0.1)',
                border: '1px solid red',
                color: 'red',
                padding: '10px',
                borderRadius: '5px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div style={{
                background: 'rgba(255,215,0,0.1)',
                border: '1px solid #FFD700',
                color: '#FFD700',
                padding: '10px',
                borderRadius: '5px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                Loading contract data...
              </div>
            )}

            {/* Contract Information */}
            {contract && (
              <div style={{
                background: 'rgba(255,215,0,0.1)',
                border: '1px solid #FFD700',
                padding: '15px',
                borderRadius: '5px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <h4 style={{ color: '#FFD700', margin: '0 0 10px 0' }}>Smart Contract Mode</h4>
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#FFD700' }}>
                  <p><strong>Contract:</strong> {WEB3CASH_CONTRACT_ADDRESS.substring(0, 10)}...{WEB3CASH_CONTRACT_ADDRESS.substring(38)}</p>
                  <p><strong>Savings APY:</strong> {contractData.savingsAPY}% | <strong>Collateral Ratio:</strong> {contractData.collateralRatio}%</p>
                  <p><strong>Fee Rate:</strong> {contractData.feePercent}%</p>
                  <p><strong>Revenue Wallet:</strong> {contractData.revenueWallet.substring(0, 10)}...</p>
                </div>
              </div>
            )}

            {/* Main Content Tabs */}
            <div style={{
              background: 'rgba(255,215,0,0.05)',
              border: '1px solid #FFD700',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <div style={{ display: 'flex', borderBottom: '1px solid #FFD700' }}>
                <button
                  onClick={() => setActiveTab('savings')}
                  style={{
                    flex: 1,
                    padding: '15px 20px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    border: 'none',
                    cursor: 'pointer',
                    background: activeTab === 'savings' ? '#FFD700' : 'transparent',
                    color: activeTab === 'savings' ? 'black' : '#FFD700',
                    borderBottom: activeTab === 'savings' ? '2px solid #FFD700' : 'none'
                  }}
                >
                  Savings
                </button>
                <button
                  onClick={() => setActiveTab('lending')}
                  style={{
                    flex: 1,
                    padding: '15px 20px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    border: 'none',
                    cursor: 'pointer',
                    background: activeTab === 'lending' ? '#FFD700' : 'transparent',
                    color: activeTab === 'lending' ? 'black' : '#FFD700',
                    borderBottom: activeTab === 'lending' ? '2px solid #FFD700' : 'none'
                  }}
                >
                  Lending
                </button>
              </div>

              <div style={{ padding: '30px' }}>
                {isAuthenticated ? (
                  <>
                    {activeTab === 'savings' && (
                      <SavingsTab
                        userAddress={userAddress}
                        contract={contract}
                        contractData={contractData}
                        loading={loading}
                        setLoading={setLoading}
                        setError={setError}
                      />
                    )}
                    {activeTab === 'lending' && (
                      <LendingTab
                        userAddress={userAddress}
                        contract={contract}
                        contractData={contractData}
                        loading={loading}
                        setLoading={setLoading}
                        setError={setError}
                      />
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <p style={{ color: '#FFD700', fontSize: '18px' }}>
                      Please connect your wallet to access Web3Cash features
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Revenue Information Footer */}
          <div
            style={{
              backgroundColor: '#1a1a1a',
              color: '#FFD700',
              padding: '1rem',
              borderRadius: '10px',
              width: '50%',
              maxWidth: '400px',
              boxShadow: '0 0 15px #FFD700',
              transition: 'box-shadow 0.3s ease',
              margin: '2rem auto',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 0 25px #FFD700')}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 0 15px #FFD700')}
          >
            <p><strong>Revenue Collection:</strong> web3reg.eth (0x74338Aea95f8e80D64944f3F2adAC4c2e7Da919a)</p>
            <p className="text-sm mt-2">All fees automatically routed to revenue wallet</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Web3Cash;
